import type { z } from "zod";
import type { Model } from "@/schemas/model.schema";
import type { PulseMessageMetadata } from "@/schemas/thread.schema";
import { formatModelPrice } from "@/utils/model-pricing";

/**
 * Which duration drove the tokens/s metric of a message report. Literal
 * union declared once here and reused by MessageReport (core/types.ts), the
 * MessageMetrics prop and SPEED_TOOLTIP_KEYS.
 */
export type SpeedDurationSource = "generation_time" | "execution_time";

/** Tooltip key per speed source: the label must describe what was computed. */
export const SPEED_TOOLTIP_KEYS: Record<SpeedDurationSource, "agents:message_metrics.generation_speed_tooltip" | "agents:message_metrics.speed_tooltip"> = {
  generation_time: "agents:message_metrics.generation_speed_tooltip",
  execution_time: "agents:message_metrics.speed_tooltip",
};

/**
 * Picks the duration the speed metric uses: the server-reported
 * `generation_time` (first streamed token of the model call -> its report)
 * whenever the server sent it, otherwise the legacy whole-call
 * `execution_time` (includes our own auth/credit/persistence overhead).
 * A 0 ms generation (single chunk) stays a generation measurement: it yields
 * no speed instead of silently switching to the whole-call duration.
 */
export function resolveSpeedDuration({
  generation_time,
  execution_time,
}: Pick<z.infer<typeof PulseMessageMetadata>, "generation_time" | "execution_time">): {
  durationMs: number;
  source: SpeedDurationSource;
} {
  if (generation_time !== undefined && generation_time >= 0) {
    return { durationMs: generation_time, source: "generation_time" };
  }
  return { durationMs: execution_time ?? 0, source: "execution_time" };
}

export function calculateSpeed({
  durationMs,
  completionTokens,
}: {
  durationMs: number;
  completionTokens: number;
}) {
  if (!durationMs) return 0;
  if (!completionTokens) return 0;
  return completionTokens / (durationMs / 1000);
}

export function getModelPrice(model: Model, currency: string) {
  return formatModelPrice(model.name, currency);
}
